import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import ErrorMessage from '../components/ErrorMessage'
import LoadingProgress from '../components/LoadingProgress'
import Calendar from '../components/Calendar'; // Import the Calendar component
import { fetchCalendar } from '../actions/calendars'
import { fetchCustomer } from '../actions/customers'
import { fetchContracts } from '../actions/contracts'
import { getMachineCoordinates } from '../utils/machines'
import CalendarPricesAddForm from '../forms/CalendarPricesAddForm'
import '../styles/CalendarDetailsView.css'

class CalendarDetailsView extends React.Component {
  constructor(props) {
    super(props);
    const currentYear = new Date().getFullYear();
    this.state = {
      startMonth: 10,
      endMonth: 3,
      startYear: currentYear,
      endYear: currentYear,
      minYear: currentYear,
      maxYear: currentYear,
      showControls: false,
      weatherData: {},
      weatherLoading: false,
      weatherLocation: null,
    };
    this.lastWeatherFetchKey = null;
  }

  componentDidMount() {
    const { fetchCalendar, fetchCustomer, fetchContracts, contracts } = this.props;

    fetchCalendar(this.props.match.params.id).then(response => {
      fetchCustomer(response.response.Customer);
    });

    if (!contracts) {
      fetchContracts();
    }
  }

  checkAndFetchWeather() {
    const { contracts, calendar } = this.props;
    const { startMonth, endMonth, startYear, endYear } = this.state;
    if (!contracts || !calendar || !this.rangeInitialized) return;

    // Find the location from any active contract for the same Customer
    let coords = this.state.weatherLocation;
    if (!coords) {
      const customerId = calendar.Customer;
      const contract = contracts.find(c => {
        if (!c.Location) return false;
        const custId = c.Customer?._id || c.Customer;
        return custId?.toString() === customerId?.toString();
      });
      if (contract) {
        coords = getMachineCoordinates(contract.Location);
        if (coords) {
          this.setState({ weatherLocation: coords });
        }
      }
    }

    if (!coords) return;

    const pad = n => String(n).padStart(2, '0');
    const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    const startDate = `${startYear}-${pad(startMonth + 1)}-01`;
    const endDate = `${endYear}-${pad(endMonth + 1)}-${pad(daysInMonth(endYear, endMonth))}`;
    const fetchKey = `${coords.lat},${coords.lng}|${startDate}|${endDate}`;

    if (fetchKey !== this.lastWeatherFetchKey) {
      this.lastWeatherFetchKey = fetchKey;
      this.fetchWeatherForRange(coords.lat, coords.lng, startDate, endDate);
    }
  }

  fetchWeatherForRange = async (lat, lng, startDate, endDate) => {
    this.setState({ weatherLoading: true });
    const todayDate = new Date();
    const todayStr = todayDate.toISOString().split('T')[0];
    const yDate = new Date(todayDate);
    yDate.setDate(yDate.getDate() - 1);
    const yesterdayStr = yDate.toISOString().split('T')[0];
    const maxFDate = new Date(todayDate);
    maxFDate.setDate(maxFDate.getDate() + 15);
    const maxForecastStr = maxFDate.toISOString().split('T')[0];

    const weatherData = {};
    const fetchPromises = [];

    // Archive API: past dates up to yesterday
    if (startDate <= yesterdayStr) {
      const archiveEnd = endDate <= yesterdayStr ? endDate : yesterdayStr;
      const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=${startDate}&end_date=${archiveEnd}&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=auto`;
      fetchPromises.push(
        fetch(url).then(r => r.json()).then(data => {
          if (data.daily?.time) {
            data.daily.time.forEach((date, i) => {
              weatherData[date] = {
                code: data.daily.weathercode?.[i] ?? null,
                tmax: data.daily.temperature_2m_max?.[i] ?? null,
                tmin: data.daily.temperature_2m_min?.[i] ?? null,
                precip: data.daily.precipitation_sum?.[i] ?? null,
              };
            });
          }
        }).catch(() => {})
      );
    }

    // Forecast API: today and up to 15 days ahead
    if (endDate >= todayStr) {
      const forecastStart = startDate >= todayStr ? startDate : todayStr;
      const forecastEnd = endDate <= maxForecastStr ? endDate : maxForecastStr;
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&start_date=${forecastStart}&end_date=${forecastEnd}&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=auto`;
      fetchPromises.push(
        fetch(url).then(r => r.json()).then(data => {
          if (data.daily?.time) {
            data.daily.time.forEach((date, i) => {
              weatherData[date] = {
                code: data.daily.weathercode?.[i] ?? null,
                tmax: data.daily.temperature_2m_max?.[i] ?? null,
                tmin: data.daily.temperature_2m_min?.[i] ?? null,
                precip: data.daily.precipitation_sum?.[i] ?? null,
              };
            });
          }
        }).catch(() => {})
      );
    }

    try {
      await Promise.all(fetchPromises);
      this.setState({ weatherData, weatherLoading: false });
    } catch (err) {
      console.error('Weather fetch error:', err);
      this.setState({ weatherLoading: false });
    }
  };

  componentDidUpdate(prevProps) {
    // Only initialise month/year range from data the first time the calendar
    // arrives. On subsequent refetches (after add / modify / delete) we keep
    // whatever the user is currently viewing so their position is preserved.
    if (
      !this.rangeInitialized &&
      this.props.calendar &&
      this.props.calendar.PriceCalendar &&
      this.props.calendar.PriceCalendar.length > 0
    ) {
      // For the year dropdown range, use all entries regardless of fill
      const allDates = this.props.calendar.PriceCalendar.map(entry => new Date(entry.date));
      const minYear = new Date(Math.min(...allDates)).getFullYear();
      const maxYear = new Date(Math.max(...allDates)).getFullYear();

      this.rangeInitialized = true;

      // Default view to the current month/year
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      // Find the last month with a filled price value in the current year
      const filledCurrentYear = this.props.calendar.PriceCalendar.filter(entry => {
        const d = new Date(entry.date);
        return d.getFullYear() === currentYear &&
          entry.prices && entry.prices.some(p => p && typeof p.price === 'number');
      });
      const lastFilledMonth = filledCurrentYear.length > 0
        ? new Date(Math.max(...filledCurrentYear.map(e => new Date(e.date)))).getMonth()
        : currentMonth;

      this.setState({
        startMonth: currentMonth,
        endMonth: lastFilledMonth,
        startYear: currentYear,
        endYear: currentYear,
        minYear,
        maxYear,
      }, () => this.checkAndFetchWeather());
    } else if (
      this.rangeInitialized &&
      this.props.calendar &&
      this.props.calendar.PriceCalendar &&
      prevProps.calendar?.PriceCalendar !== this.props.calendar.PriceCalendar &&
      this.props.calendar.PriceCalendar.length > 0
    ) {
      // Keep minYear / maxYear in sync (new years might have been added)
      // but do NOT touch the currently displayed startMonth/endMonth/startYear/endYear.
      const dates = this.props.calendar.PriceCalendar.map(entry => new Date(entry.date));
      const minYear = new Date(Math.min(...dates)).getFullYear();
      const maxYear = new Date(Math.max(...dates)).getFullYear();
      if (minYear !== this.state.minYear || maxYear !== this.state.maxYear) {
        this.setState({ minYear, maxYear });
      }
    }

    this.checkAndFetchWeather();
  }

  handleMonthChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: parseInt(value) });
  };

  handleYearChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: parseInt(value) });
  };

  render() {
    const { calendar, error } = this.props;
    const { startMonth, endMonth, startYear, endYear, minYear, maxYear, showControls, weatherData, weatherLocation, weatherLoading } = this.state;

    if (error) {
      return <ErrorMessage message={error.message} />;
    }

    // Only block the whole page on the *initial* load (when we have no
    // calendar yet). Subsequent add / modify / delete operations toggle
    // `loading` in the reducer, but we don't want to unmount the view —
    // that is what caused losing the scroll position after each submit.
    if (!calendar) {
      return <LoadingProgress />;
    }

    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    // Generate array of years from minYear to maxYear
    const availableYears = [];
    for (let year = minYear; year <= maxYear; year++) {
      availableYears.push(year);
    }

    const datesForRange = (calendar.PriceCalendar || [])
      .map(pc => { const s = typeof pc.date === 'string' ? pc.date.split('T')[0] : ''; return s || null })
      .filter(Boolean)
      .sort()
    const startDateLabel = datesForRange.length ? new Date(datesForRange[0] + 'T00:00:00Z').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—'
    const endDateLabel = datesForRange.length ? new Date(datesForRange[datesForRange.length-1] + 'T00:00:00Z').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—'
    const entriesCount = (calendar.PriceCalendar || []).length

    const priceValues = (calendar.PriceCalendar || []).reduce((acc, pc) => {
      if (pc.prices && pc.prices.length) pc.prices.forEach(p => { if (p && typeof p.price === 'number') acc.push(p.price) })
      return acc
    }, [])
    const minPrice = priceValues.length ? Math.min(...priceValues) : null
    const maxPrice = priceValues.length ? Math.max(...priceValues) : null
    const fmt = v => v === null ? '—' : v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })

    // View-range stats (currently selected months)
    // Parse year/month from the date string directly to avoid any timezone offset issues
    const parseYM = (date) => {
      const s = typeof date === 'string' ? date : new Date(date).toISOString()
      const parts = s.split('T')[0].split('-')
      return { y: parseInt(parts[0], 10), m: parseInt(parts[1], 10) - 1 }
    }
    const viewEntries = (calendar.PriceCalendar || []).filter(pc => {
      if (!pc.date) return false
      const { y, m } = parseYM(pc.date)
      if (isNaN(y) || isNaN(m)) return false
      if (startYear === endYear) return y === startYear && m >= startMonth && m <= endMonth
      if (y === startYear) return m >= startMonth
      if (y === endYear) return m <= endMonth
      return y > startYear && y < endYear
    })
    const viewPrices = viewEntries.reduce((acc, pc) => {
      if (pc.prices) pc.prices.forEach(p => { if (p && typeof p.price === 'number') acc.push(p.price) })
      return acc
    }, [])
    const viewMin = viewPrices.length ? Math.min(...viewPrices) : null
    const viewMax = viewPrices.length ? Math.max(...viewPrices) : null
    const viewAvg = viewPrices.length ? viewPrices.reduce((s,v)=>s+v,0)/viewPrices.length : null
    const monthNamesShort = [
      'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
      'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
    ]
    const viewLabel = startYear === endYear && startMonth === endMonth
      ? `${monthNamesShort[startMonth]} ${startYear}`
      : `${monthNamesShort[startMonth]} ${startYear} — ${monthNamesShort[endMonth]} ${endYear}`

    // Detect missing days in the viewed range
    const existingDates = new Set(
      (calendar.PriceCalendar || []).map(pc =>
        typeof pc.date === 'string' ? pc.date.split('T')[0] : ''
      ).filter(Boolean)
    )
    const missingDays = []
    const pad = n => String(n).padStart(2, '0')
    for (let y = startYear; y <= endYear; y++) {
      const mStart = y === startYear ? startMonth : 0
      const mEnd = y === endYear ? endMonth : 11
      for (let mo = mStart; mo <= mEnd; mo++) {
        const daysInMonth = new Date(y, mo + 1, 0).getDate()
        for (let d = 1; d <= daysInMonth; d++) {
          const key = `${y}-${pad(mo + 1)}-${pad(d)}`
          if (!existingDates.has(key)) missingDays.push(key)
        }
      }
    }

    // ── Weather summary for the info card (shown when no missing days) ──────
    let weatherSummary = null;
    if (missingDays.length === 0 && weatherData && Object.keys(weatherData).length > 0) {
      const { customer, category } = this.props;
      const getWCat = code => {
        if (code === null || code === undefined) return null;
        if (code <= 2)  return 'sunny';
        if (code === 3) return 'cloudy';
        if (code <= 48) return 'foggy';
        if (code <= 67) return 'rainy';
        if (code <= 77) return 'snowy';
        if (code <= 82) return 'showery';
        if (code >= 95) return 'stormy';
        return 'cloudy';
      };
      const getZN = zone => {
        if (!customer || !customer.Zones) return String(zone);
        const found = customer.Zones.find(z => z.Zone === zone);
        return found ? found.Description : String(zone);
      };
      const getTN = type => {
        if (category === 'BAQUEIRA') {
          const baq = { BN3XL: '2 PAX', CN3XL: '3 PAX', BW3XL: '4 PAX' };
          return baq[type] || type;
        }
        const map = {
          BNS: 'Standard', CNS: 'Standard Carga', BWS: 'Standard XL', CWS: 'Standard Carga XL',
          BNM: 'Medium',   CNM: 'Medium Carga',   BWM: 'Medium XL',   CWM: 'Medium Carga XL',
          BNL: 'Large',    CNL: 'Large Carga',    BWL: 'Large XL',    CWL: 'Large Carga XL',
          BNXL: 'Large 4', CNXL: 'Large 4 Carga', BWXL: 'Large 4 XL', CWXL: 'Large 4 Carga XL',
          BN2XL: 'Large 5', CN2XL: 'Large 5 Carga', BW2XL: 'Large 5 XL', CW2XL: 'Large 5 Carga XL',
          BN3XL: 'Large 6', CN3XL: 'Large 6 Carga', BW3XL: 'Large 6 XL', CW3XL: 'Large 6 Carga XL',
        };
        return map[type] || type;
      };
      const catMeta = {
        sunny:   { label: 'Soleado',   icon: 'fa-sun',                    color: '#d97706' },
        cloudy:  { label: 'Nublado',   icon: 'fa-cloud',                  color: '#64748b' },
        foggy:   { label: 'Niebla',    icon: 'fa-smog',                   color: '#94a3b8' },
        rainy:   { label: 'Lluvia',    icon: 'fa-cloud-rain',             color: '#2563eb' },
        snowy:   { label: 'Nieve',     icon: 'fa-snowflake',              color: '#0ea5e9' },
        showery: { label: 'Chubascos', icon: 'fa-cloud-showers-heavy',    color: '#1d4ed8' },
        stormy:  { label: 'Tormenta',  icon: 'fa-bolt',                   color: '#7c3aed' },
      };
      const catCounts = {};
      const catPrices = {};
      // per zone+type: { good: [], bad: [], snow: [] }
      const typeMap = {};
      viewEntries.forEach(entry => {
        const dateStr = typeof entry.date === 'string' ? entry.date.split('T')[0] : new Date(entry.date).toISOString().split('T')[0];
        const w = weatherData[dateStr];
        if (!w || w.code === null) return;
        const cat = getWCat(w.code);
        if (!cat) return;
        catCounts[cat] = (catCounts[cat] || 0) + 1;
        const prices = (entry.prices || []).filter(p => typeof p.price === 'number');
        prices.forEach(p => {
          // aggregate all prices for overall tips
          if (!catPrices[cat]) catPrices[cat] = [];
          catPrices[cat].push(p.price);
          // per-type breakdown
          const key = `${p.zone}::${p.type}`;
          if (!typeMap[key]) typeMap[key] = { zone: p.zone, type: p.type, good: [], bad: [], snow: [] };
          const bucket = ['sunny','cloudy'].includes(cat) ? 'good'
            : ['rainy','showery','stormy'].includes(cat) ? 'bad'
            : cat === 'snowy' ? 'snow' : null;
          if (bucket) typeMap[key][bucket].push(p.price);
        });
      });
      const totalWithWeather = Object.values(catCounts).reduce((s, v) => s + v, 0);
      if (totalWithWeather > 0) {
        const sortedCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]);
        const wavg = arr => arr && arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null;
        const goodAvg = wavg([...(catPrices.sunny || []), ...(catPrices.cloudy || [])]);
        const badAvg  = wavg([...(catPrices.rainy || []), ...(catPrices.showery || []), ...(catPrices.stormy || [])]);
        const wfmt = v => v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const tips = [];
        const goodDays = (catCounts.sunny || 0) + (catCounts.cloudy || 0);
        const badDays  = (catCounts.rainy  || 0) + (catCounts.showery || 0) + (catCounts.stormy || 0);
        const snowDays = catCounts.snowy || 0;
        const fogDays  = catCounts.foggy || 0;
        const snowAvg  = wavg(catPrices.snowy || []);
        const stormAvg = wavg(catPrices.stormy || []);
        if (goodAvg !== null && badAvg !== null) {
          const diff = goodAvg - badAvg;
          if (diff > 0.5) {
            tips.push({ icon: 'fa-arrow-trend-up', color: '#16a34a', text: `Precio medio en días buenos: ${wfmt(goodAvg)}€ vs ${wfmt(badAvg)}€ en lluvia. Alta demanda con buen tiempo — considera subir tarifas.` });
          } else if (diff < -0.5) {
            tips.push({ icon: 'fa-arrow-trend-down', color: '#dc2626', text: `Los días de lluvia tienen precio medio (${wfmt(badAvg)}€) superior a los soleados (${wfmt(goodAvg)}€). Revisa si las tarifas están optimizadas.` });
          } else {
            tips.push({ icon: 'fa-equals', color: '#64748b', text: `Los precios son estables independientemente del clima (${wfmt(goodAvg)}€ sol vs ${wfmt(badAvg)}€ lluvia). Buen equilibrio.` });
          }
        }
        if (goodDays >= totalWithWeather * 0.6) {
          tips.push({ icon: 'fa-sun', color: '#d97706', text: 'Período dominado por buen tiempo — rango favorable para demanda turística alta.' });
        } else if (badDays >= totalWithWeather * 0.4) {
          tips.push({ icon: 'fa-umbrella', color: '#2563eb', text: 'Alta proporción de días de lluvia. Considera tarifas dinámicas o descuentos para incentivar reservas.' });
        }
        if (snowDays >= totalWithWeather * 0.15) {
          const snowNote = snowAvg !== null ? ` Precio medio en nieve: ${wfmt(snowAvg)}€.` : '';
          tips.push({ icon: 'fa-snowflake', color: '#0ea5e9', text: `${snowDays} días de nieve en el rango (${Math.round(snowDays / totalWithWeather * 100)}%).${snowNote} Considera tarifa especial para días de nieve.` });
        }
        if (stormAvg !== null && goodAvg !== null && stormAvg < goodAvg - 0.5) {
          tips.push({ icon: 'fa-bolt', color: '#7c3aed', text: `Los días de tormenta muestran precios inferiores (${wfmt(stormAvg)}€) — posible oportunidad de tarifa mínima garantizada.` });
        }
        if (fogDays >= totalWithWeather * 0.1) {
          tips.push({ icon: 'fa-smog', color: '#94a3b8', text: `${fogDays} días de niebla detectados (${Math.round(fogDays / totalWithWeather * 100)}%). Visibilidad reducida puede afectar a la afluencia.` });
        }
        const overallAvg = wavg(Object.values(catPrices).flat());
        if (overallAvg !== null) {
          tips.push({ icon: 'fa-chart-bar', color: '#6366f1', text: `Precio promedio global en el período: ${wfmt(overallAvg)}€ sobre ${totalWithWeather} días con datos climáticos.` });
        }
        // per-type tips: only generate when both good and bad averages exist and differ meaningfully
        const typeTips = Object.values(typeMap)
          .map(item => {
            const ga = wavg(item.good), ba = wavg(item.bad), sa = wavg(item.snow);
            if (ga === null && ba === null) return null;
            const label = `${getZN(item.zone)} · ${getTN(item.type)}`;
            if (ga !== null && ba !== null) {
              const diff = ga - ba;
              if (diff > 0.5) return { label, icon: 'fa-arrow-trend-up',   color: '#16a34a', goodAvg: ga, badAvg: ba, snowAvg: sa, dir: 'up' };
              if (diff < -0.5) return { label, icon: 'fa-arrow-trend-down', color: '#dc2626', goodAvg: ga, badAvg: ba, snowAvg: sa, dir: 'down' };
              return { label, icon: 'fa-equals', color: '#64748b', goodAvg: ga, badAvg: ba, snowAvg: sa, dir: 'eq' };
            }
            if (ga !== null) return { label, icon: 'fa-sun',  color: '#d97706', goodAvg: ga, badAvg: null, snowAvg: sa, dir: 'info' };
            return { label, icon: 'fa-cloud-rain', color: '#2563eb', goodAvg: null, badAvg: ba, snowAvg: sa, dir: 'info' };
          })
          .filter(Boolean)
          .sort((a, b) => { const o = { up: 0, down: 1, eq: 2, info: 3 }; return (o[a.dir] ?? 3) - (o[b.dir] ?? 3); });
        weatherSummary = { sortedCats, catMeta, totalWithWeather, tips, typeTips };
      }
    }

    return (
      <div className="calendar-details-view">
        <div className="calendar-details-header mb-3">
          <div className="calendar-details-title">
            <h1 className="m-0">
              <i className="fas fa-calendar-alt mr-2"></i>
              <span className="text-vw-light">Calendario</span>
              <span className="text-vw-dark"> {calendar.Name}</span>
            </h1>
          </div>
          <div className="calendar-details-actions d-flex align-items-center" style={{gap: '12px'}}>
            <div className="header-stat-group">
              <span className="header-stat-group-label">Vista actual · {viewLabel}</span>
              <div className="header-stat-pills">
                <span className="header-stat-pill"><i className="fas fa-calendar-check mr-1"></i>{viewEntries.length} días</span>
                <span className="header-stat-pill low"><i className="fas fa-arrow-down mr-1"></i>{fmt(viewMin)}</span>
                <span className="header-stat-pill avg"><i className="fas fa-minus mr-1"></i>{fmt(viewAvg)}</span>
                <span className="header-stat-pill high"><i className="fas fa-arrow-up mr-1"></i>{fmt(viewMax)}</span>
              </div>
            </div>
            <div className="header-stat-divider"></div>
            <div className="header-stat-group">
              <span className="header-stat-group-label">Todo el calendario</span>
              <div className="header-stat-pills">
                <span className="header-stat-pill"><i className="fas fa-calendar-check mr-1"></i>{entriesCount} días</span>
                <span className="header-stat-pill"><i className="fas fa-arrows-alt-h mr-1"></i>{startDateLabel} — {endDateLabel}</span>
                <span className="header-stat-pill low"><i className="fas fa-arrow-down mr-1"></i>{fmt(minPrice)}</span>
                <span className="header-stat-pill high"><i className="fas fa-arrow-up mr-1"></i>{fmt(maxPrice)}</span>
              </div>
            </div>
            <div className="header-stat-divider"></div>
            <button
              className="calendar-toggle-btn"
              onClick={() => this.setState(s => ({ showControls: !s.showControls }))}
            >
              {showControls ? (
                <span key="open"><i className="fas fa-chevron-up mr-1"></i>Ocultar controles</span>
              ) : (
                <span key="closed"><i className="fas fa-chevron-down mr-1"></i>Mostrar controles</span>
              )}
            </button>
          </div>
        </div>

        {showControls && (
        <div className="row mb-3">
          <div className="col-xs-12 col-sm-4 col-md-3 mb-2">
            <div className="ctrl-card">
              <div className="ctrl-card-header"><i className="fas fa-eye mr-1"></i>Vista</div>
              <div className="ctrl-card-body">
                <div className="ctrl-field-group">
                  <label className="ctrl-label">Inicio</label>
                  <div className="ctrl-row">
                    <select className="ctrl-select" name="startMonth" value={startMonth} onChange={this.handleMonthChange}>
                      {monthNames.map((monthName, index) => (
                        <option key={index} value={index}>{monthName}</option>
                      ))}
                    </select>
                    <select className="ctrl-select" name="startYear" value={startYear} onChange={this.handleYearChange}>
                      {availableYears.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="ctrl-field-group">
                  <label className="ctrl-label">Fin</label>
                  <div className="ctrl-row">
                    <select className="ctrl-select" name="endMonth" value={endMonth} onChange={this.handleMonthChange}>
                      {monthNames.map((monthName, index) => (
                        <option key={index} value={index}>{monthName}</option>
                      ))}
                    </select>
                    <select className="ctrl-select" name="endYear" value={endYear} onChange={this.handleYearChange}>
                      {availableYears.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xs-12 col-sm-8 col-md-9 mb-2">
            <div className="ctrl-card">
              <div className="ctrl-card-header"><i className="fas fa-edit mr-1"></i>Actualizar precios</div>
              <div className="ctrl-card-body">
                <CalendarPricesAddForm
                  customer={this.props.customer}
                  calendar={calendar}
                  contracts={this.props.contracts}
                />
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="cib-card mb-3">
          <div className="cib-left">
            <p className="cib-section-title"><i className="fas fa-book-open mr-1"></i>Cómo funciona</p>
            <ul className="cib-list">
              <li><i className="fas fa-circle cib-dot covered"></i>Días con precio configurado aparecen con fondo claro <strong>verdoso</strong>.</li>
              <li><i className="fas fa-circle cib-dot missing"></i>Días sin ningún precio aparecen con fondo <strong>rojizo</strong> — pueden causar errores en reservas.</li>
              <li><i className="fas fa-sliders-h cib-dot neutral"></i>Usa <strong>Mostrar controles</strong> para cambiar el rango de meses visible.</li>
              <li><i className="fas fa-edit cib-dot neutral"></i>Para añadir o eliminar precios, abre los controles y usa el formulario de actualización.</li>
            </ul>
          </div>
          {missingDays.length > 0 ? (
            <div className="cib-right">
              <p className="cib-section-title cib-warn-title"><i className="fas fa-exclamation-triangle mr-1"></i>{missingDays.length} día{missingDays.length > 1 ? 's' : ''} sin entrada en la vista actual</p>
              <div className="cib-missing-list">
                {missingDays.slice(0, 30).map(d => (
                  <span key={d} className="cib-missing-day">{new Date(d + 'T00:00:00Z').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' })}</span>
                ))}
                {missingDays.length > 30 && <span className="cib-missing-more">+{missingDays.length - 30} más</span>}
              </div>
            </div>
          ) : weatherSummary ? (
            <div className="cib-right cib-weather">
              <p className="cib-section-title cib-weather-title"><i className="fas fa-cloud-sun mr-1"></i>Clima &amp; precios &middot; {viewLabel}</p>
              <div className="cib-weather-cols">
                <div className="cib-wdist">
                  {weatherSummary.sortedCats.slice(0, 5).map(([cat, count]) => {
                    const meta = weatherSummary.catMeta[cat];
                    const pct = Math.round(count / weatherSummary.totalWithWeather * 100);
                    return (
                      <div key={cat} className="cib-wdist-row">
                        <i className={`fas ${meta.icon} cib-wdist-icon`} style={{ color: meta.color }}></i>
                        <span className="cib-wdist-label">{meta.label}</span>
                        <div className="cib-wdist-bar-track">
                          <div className="cib-wdist-bar-fill" style={{ width: `${pct}%`, background: meta.color }}></div>
                        </div>
                        <span className="cib-wdist-pct">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
  <div className="cib-weather-tips-col">
                  {weatherSummary.tips.length > 0 && (
                    <div className="cib-weather-tips">
                      {weatherSummary.tips.map((tip, i) => (
                        <div key={i} className="cib-tip">
                          <i className={`fas ${tip.icon} cib-tip-icon`} style={{ color: tip.color }}></i>
                          <span>{tip.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="cib-weather-disclaimer">
                <i className="fas fa-info-circle mr-1"></i>
                Datos de Open-Meteo (histórico + previsión hasta 15 días). Las recomendaciones son orientativas.
              </div>
            </div>
          ) : null}
        </div>

        <div className="row mb-3">
          <div className="col">
            <Calendar
              startYear={startYear}
              endYear={endYear}
              priceCalendar={calendar.PriceCalendar}
              startMonth={startMonth}
              endMonth={endMonth}
              customer={this.props.customer}
              weatherData={weatherData}
              weatherLoading={weatherLoading}
              weatherLocation={weatherLocation}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  calendar: state.calendars.calendar,
  customer: state.customers.customer,
  contracts: state.contracts.contracts,
  category: state.calendars.calendar ? state.calendars.calendar.Category : null,
  loading: state.calendars.loading,
  error: state.calendars.error,
});

const mapDispatchToProps = dispatch => ({
  fetchCalendar: bindActionCreators(fetchCalendar, dispatch),
  fetchCustomer: bindActionCreators(fetchCustomer, dispatch),
  fetchContracts: bindActionCreators(fetchContracts, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(CalendarDetailsView);
